import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Icon } from "@/lib/ui";
import { Loader2 } from "lucide-react";
import {
  BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip, PieChart, Pie, Cell, LineChart, Line,
} from "recharts";

const STAT_CARDS = [
  { key: "total_tags", label: "Total Tags", icon: "Tags", color: "#0A0A0A" },
  { key: "claimed", label: "Claimed", icon: "ShieldCheck", color: "#007AFF" },
  { key: "active", label: "Active", icon: "CircleCheck", color: "#34C759" },
  { key: "lost", label: "Lost", icon: "TriangleAlert", color: "#FF3B30" },
  { key: "unassigned", label: "Unassigned", icon: "CircleDashed", color: "#8E8E93" },
  { key: "total_users", label: "Users", icon: "Users", color: "#5856D6" },
  { key: "scans_today", label: "Scans Today", icon: "Radar", color: "#FF9500" },
  { key: "finder_messages", label: "Finder Messages", icon: "MessagesSquare", color: "#AF52DE" },
];
const PIE = ["#007AFF", "#FF3B30", "#8E8E93", "#A16207", "#34C759", "#5856D6", "#FF9500", "#AF52DE"];

export default function AdminDashboard() {
  const [s, setS] = useState(null);
  useEffect(() => { api.get("/admin/stats").then((r) => setS(r.data.data)); }, []);
  if (!s) return <div className="py-24 grid place-items-center"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>;

  return (
    <div className="space-y-8">
      <div>
        <h1 className="font-display font-black text-3xl tracking-tight">Dashboard</h1>
        <p className="text-muted-foreground mt-1">Live overview of the tag network.</p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {STAT_CARDS.map((c) => (
          <div key={c.key} className="rounded-2xl border border-border bg-background p-5" data-testid={`stat-${c.key}`}>
            <div className="flex items-center justify-between">
              <span className="w-9 h-9 rounded-lg grid place-items-center" style={{ backgroundColor: c.color + "1A", color: c.color }}>
                <Icon name={c.icon} className="w-4.5 h-4.5" />
              </span>
            </div>
            <p className="font-display font-black text-3xl mt-3">{(s[c.key] ?? 0).toLocaleString()}</p>
            <p className="text-sm text-muted-foreground">{c.label}</p>
          </div>
        ))}
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 rounded-2xl border border-border bg-background p-6">
          <h3 className="font-display font-bold mb-4">Scans — last 7 days</h3>
          <ResponsiveContainer width="100%" height={260}>
            <LineChart data={s.scan_series}>
              <XAxis dataKey="day" stroke="#8E8E93" fontSize={12} tickLine={false} axisLine={false} />
              <YAxis stroke="#8E8E93" fontSize={12} tickLine={false} axisLine={false} allowDecimals={false} />
              <Tooltip />
              <Line type="monotone" dataKey="scans" stroke="#007AFF" strokeWidth={3} dot={{ r: 4 }} />
            </LineChart>
          </ResponsiveContainer>
        </div>
        <div className="rounded-2xl border border-border bg-background p-6">
          <h3 className="font-display font-bold mb-4">Status split</h3>
          <ResponsiveContainer width="100%" height={260}>
            <PieChart>
              <Pie data={s.status_distribution} dataKey="count" nameKey="name" innerRadius={55} outerRadius={90} paddingAngle={3}>
                {s.status_distribution.map((e, i) => <Cell key={i} fill={PIE[i % PIE.length]} />)}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="rounded-2xl border border-border bg-background p-6">
        <h3 className="font-display font-bold mb-4">Tags by category</h3>
        <ResponsiveContainer width="100%" height={280}>
          <BarChart data={s.category_distribution}>
            <XAxis dataKey="name" stroke="#8E8E93" fontSize={12} tickLine={false} axisLine={false} />
            <YAxis stroke="#8E8E93" fontSize={12} tickLine={false} axisLine={false} allowDecimals={false} />
            <Tooltip />
            <Bar dataKey="count" fill="#0A0A0A" radius={[6, 6, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
